// The user's Gmail link (the Gmail add-on "Ticket aus Mail"): a
// row on the account page, the twin of `GoogleConnection`. The add-on's
// requests carry a Google account, never a Loom session; this row is where a
// user tells Loom which Google account is theirs.
//
// Everything it shows comes from `GET /api/gmail/link`:
//
//   - nothing     `enabled: false` or a 404 (the add-on is off in this
//                 environment, `onAvailability("off")`), or the read failed
//                 (`"failed"`, so the page can say so).
//   - not linked  "Gmail verbinden", a full-page navigation to
//                 `/auth/gmail/start` (Google's account picker).
//   - linked      "Gmail ist verbunden." and "Trennen". Unlinking asks
//                 nothing first: it holds no credential, and linking again
//                 is one click.
//
// A link comes back here with the callback's `?gmail=` marker: the outcome
// line reads it once and the marker leaves the URL (the `from` the page's
// Back uses stays). `URLSearchParams`, never a regex: safari14 has no
// lookbehind.

import { useCallback, useEffect, useId, useRef, useState } from "react";

import type { ConnectionAvailability } from "../components/GitHubConnection";
import { useHereLocation } from "../focus/figma/useHereLocation";
import { useT, type MessageKey } from "../i18n/index.js";
import { HttpError } from "../lib/http";

import { GMAIL_LINK_START_HREF, GMAIL_RESULT_PARAM, readGmailLink, unlinkGmail } from "./gmailApi";

/** The callback's markers (`apps/server/src/http/gmail-link.ts`). */
const RESULT_COPY: Readonly<Record<string, MessageKey>> = {
  linked: "account.gmail.linked",
  domain_refused: "account.gmail.result.domainRefused",
  linked_elsewhere: "account.gmail.result.linkedElsewhere",
  error: "account.gmail.result.error",
};

const resultKeyOf = (raw: string | null): MessageKey | null =>
  raw !== null && Object.prototype.hasOwnProperty.call(RESULT_COPY, raw)
    ? (RESULT_COPY[raw] ?? null)
    : null;

const withoutMarker = (pathname: string, search: string): string => {
  const params = new URLSearchParams(search);
  params.delete(GMAIL_RESULT_PARAM);
  const rest = params.toString();
  return rest === "" ? pathname : `${pathname}?${rest}`;
};

type View =
  | { readonly kind: "loading" }
  | { readonly kind: "hidden" }
  | { readonly kind: "not_linked" }
  | { readonly kind: "linked" };

type Note = "unlinked" | "failed" | null;

export function GmailConnection({
  onAvailability,
}: {
  /** Once per settled read: `"off"` when the add-on is off here, `"failed"`
   *  on an error, `"shown"` when the row renders. */
  readonly onAvailability?: (availability: ConnectionAvailability) => void;
}) {
  const t = useT();
  const onAvailabilityRef = useRef(onAvailability);
  onAvailabilityRef.current = onAvailability;
  const { here, replace } = useHereLocation();
  const [resultKey, setResultKey] = useState<MessageKey | null>(() =>
    resultKeyOf(new URLSearchParams(here.search).get(GMAIL_RESULT_PARAM)),
  );
  const [view, setView] = useState<View>({ kind: "loading" });
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<Note>(null);
  const alive = useRef(true);
  const generation = useRef(0);
  const headId = useId();

  useEffect(() => {
    const params = new URLSearchParams(here.search);
    if (!params.has(GMAIL_RESULT_PARAM)) return;
    setResultKey(resultKeyOf(params.get(GMAIL_RESULT_PARAM)));
    replace(`${withoutMarker(here.pathname, here.search)}${here.hash}`);
  }, [here.pathname, here.search, here.hash, replace]);

  const load = useCallback(async (): Promise<void> => {
    const mine = ++generation.current;
    let next: View;
    let availability: ConnectionAvailability;
    try {
      const status = await readGmailLink();
      if (!status.enabled) {
        next = { kind: "hidden" };
        availability = "off";
      } else {
        next = status.linked === true ? { kind: "linked" } : { kind: "not_linked" };
        availability = "shown";
      }
    } catch (err) {
      next = { kind: "hidden" };
      availability = err instanceof HttpError && err.status === 404 ? "off" : "failed";
    }
    if (alive.current && mine === generation.current) {
      setView(next);
      onAvailabilityRef.current?.(availability);
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    void load();
    return () => {
      alive.current = false;
    };
  }, [load]);

  const unlink = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setNote(null);
    setResultKey(null);
    try {
      await unlinkGmail();
      if (alive.current) {
        generation.current += 1;
        setView({ kind: "not_linked" });
        setNote("unlinked");
      }
    } catch {
      if (alive.current) setNote("failed");
    } finally {
      if (alive.current) setBusy(false);
    }
    await load();
  };

  if (view.kind === "loading" || view.kind === "hidden") return null;

  const sentence =
    view.kind === "linked" ? t("account.gmail.linked") : t("account.gmail.notLinked");

  const noteText =
    note === "unlinked"
      ? t("account.gmail.unlinked")
      : note === "failed"
        ? t("account.gmail.unlinkFailed")
        : resultKey !== null && !(resultKey === "account.gmail.linked" && view.kind === "linked")
          ? t(resultKey)
          : "";

  return (
    <section className="account-row" aria-labelledby={headId}>
      <h3 id={headId} className="account-row-title">
        {t("account.gmail.title")}
      </h3>
      <p className="account-row-status">{sentence}</p>
      <div className="account-row-acts">
        {view.kind === "not_linked" && (
          <a className="focus-btn" href={GMAIL_LINK_START_HREF}>
            {t("account.gmail.link")}
          </a>
        )}
        {view.kind === "linked" && (
          <button
            type="button"
            className="account-act"
            aria-label={t("account.gmail.unlink")}
            disabled={busy}
            onClick={() => void unlink()}
          >
            {t("account.gmail.unlinkShort")}
          </button>
        )}
      </div>
      {/* Always mounted, so a screen reader announces the line when it fills. */}
      <div className={"account-row-note" + (noteText === "" ? " is-empty" : "")}>
        <p className="account-row-note-text" role="status">
          {noteText}
        </p>
      </div>
    </section>
  );
}
